import _ from 'lodash';
import { createSelector } from 'reselect';
import { PAGE_ELEMENT_ID, isBackgroundElement, isPageElement } from 'crazyEditor/core/elements/utils/elementStaticUtils';
import { getHiddenElements, getVisibleElements } from 'crazyEditor/core/elements/utils/elementFilterUtils';
import update from 'react-addons-update';
import {getUpdatedElementPositionByDelta, getDraggableDirections, getMaxDeltaY} from 'crazyEditor/redux/modules/element/helpers/elementPositionHelpers';
import {getViewState} from 'crazyEditor/redux/modules/element/helpers/elementViewHelpers';
import {getNestedChildrenUpdate, getParentIdFromChildId} from 'crazyEditor/redux/modules/element/helpers/elementChildrenHelpers';
import { getUpdatedElementSizeByDirection } from 'crazyEditor/redux/modules/element/helpers/elementSizeHelpers';
import {getNormalizedTemplateTagSpecifics} from '../templateTagSet/templateTagSelectors';
import { elementTypes } from 'crazyEditor/components/Elements/index';
import {isDraftItemId} from 'shared/helpers/EbayListingsGeneralHelpers';
import {adjustLocationInStore} from 'shared/components/redux/connectWithRouter';

export const getDesktopState = (state) => state.element.present.desktop;
export const getMobileState = (state) => state.element.present.mobile;
export const getDesktopElements = (state) => state.element.present.desktop.elements;
export const getMobileElements = (state) => state.element.present.mobile.elements;
export const getNewMobileElements = (state) => state.element.present.newMobileElements;
export const getPreviousActionViewMode = (state) => {
  const closestPast = _.last(state.element.past);
  return closestPast ? closestPast.viewMode : null;
};
export const getNextActionViewMode = (state) => {
  const closestPast = _.first(state.element.future);
  return closestPast ? closestPast.viewMode : null;
};
export const getStaticDesktopElements = (state) => state.element.present.desktop.staticElements;
export const getStaticMobileElements = (state) => state.element.present.mobile.staticElements;
export const getStageLayout = (state) => state.stage.layout;
export const isMobileView = (state) => state.editor.isMobileView;
export const getSelectedElementIds = (state) => state.stage.selectedElementIds;
export const getDraggingDirection = (state) => state.stage.draggingDirection;
export const isDraggingElement = (state) => state.stage.isDraggingElement;
export const isResizingElement = (state) => state.stage.isResizingElement;

export const isDraft = (state) => isDraftItemId(_.get(state, 'routing.location.query.itemId') ? _.get(state, 'routing.location.query.itemId') : _.get(adjustLocationInStore(state), 'routing.location.query.itemId', ''));
export const getListingData = (state) => state.listingData;
const getHoveredElementId = (state) => state.stage.hoveredElementId;
const getEditingElementId = (state) => state.stage.editingElementId;
const getUpdatePreview = (state) => state.element.present.preview;
const getIsMobileOptimized = (state) => state.template.mobileOptimized;
const getLastAddedElementsIds = (state) => state.stage.lastAddedElementsIds;
const getMousePosition = state => state.stage.mousePosition;
const getMouseDragStartPosition = state => state.stage.mouseDragStartPosition;
const getViewMode = state => state.editor.viewMode;
const getElementsState = state => state.element.present;
export const isMultiSelect = state => state.stage.selectedElementIds && state.stage.selectedElementIds.length > 1;
const getBaseUrl = state => state.editor.baseUrl;
const isBulkReviseEnabled = state => state.user.bulkReviseEnabled;
const getEbayAccounts = state => state.accountPage.accounts;

function applyPreview(elements, updatePreview) {
  if (updatePreview && (elements[updatePreview[0].id])) {
    const elementsUpdate = {};
    updatePreview.forEach((preview) => {
      elementsUpdate[preview.id] = {$set: _.merge({}, elements[preview.id], preview)};
    });
    return update(elements, elementsUpdate);
  }
  return elements;
}

function extractElementsByIds(elements, ids) {
  const selectedElements = [];
  ids.forEach((selectedElementId) => {
    if (elements[selectedElementId]) {
      selectedElements.push(elements[selectedElementId]);
    }
  });
  return selectedElements;
}

export const getDragDelta = createSelector(
  [getMousePosition, getMouseDragStartPosition],
  (mousePosition, mouseDragStartPosition) => {
    if (mousePosition && mouseDragStartPosition) {
      return {deltaX: mousePosition.x - mouseDragStartPosition.x, deltaY: mousePosition.y - mouseDragStartPosition.y};
    }
  }
);

function spreadChildren(elements) {
  const elementsToUpdate = {};
  _.forEach(elements, (element) => {
    if (_.get(element, 'data.children')) {
      _.forEach(element.data.children, (child, index) => {
        elementsToUpdate[child.id] = {
          $set: {...child, parent: element},
        };
        // _.set(elementsToUpdate[element.id], `data.children[${index}].parent`, {$set: element});
      });
    }
  });
  if (!_.isEmpty(elementsToUpdate)) {
    return update(elements, elementsToUpdate);
  }
  return elements;
}

export const getElementIdForDesktopElementControlView = createSelector(
  [getSelectedElementIds],
  (selectedElementIds) => {
    const actualSelectedElementIds = _.isArray(selectedElementIds) ? selectedElementIds : [selectedElementIds];
    const parentId = getParentIdFromChildId(actualSelectedElementIds[0]);
    if (parentId) {
      return [parentId];
    }
    return actualSelectedElementIds;
  }
);

function updateEditingElement(spreadedDesktopElements, editingElementId) {
  const editingElementUpdate = {
    editingTextElement: {
      $set: true
    }
  };
  const nestedChildUpdate = getNestedChildrenUpdate(editingElementId, spreadedDesktopElements, editingElementUpdate) || {};
  return update(spreadedDesktopElements, {[editingElementId]: editingElementUpdate, ...nestedChildUpdate});
}

function addTemplateTagsData(elements, templateTagSpecifics, listingData, bulkReviseEnabled, desktopElements = null, draft) {
  const elementsUpdate = {};
  _.forEach(elements, (element, elementId) => {
    if (element.type === elementTypes.CrossSellGalleryElement || element.type === elementTypes.MobileCrossSellGalleryElement) {
      elementsUpdate[elementId] = {
        data: {
          $merge: {listingData},
        },
      };
    }
    const templateTag = (element.desktopId && desktopElements[element.desktopId]) ? desktopElements[element.desktopId].data.templateTag : element.data.templateTag;
    if (templateTagSpecifics && element.type === elementTypes.GalleryElement) {
      if (!draft) {
        const data = {
          templateTag: elementTypes.GalleryElement,
          templateTagData: templateTagSpecifics[elementTypes.GalleryElement],
          listingData,
          bulkReviseEnabled,
        };
        const pictureURLs = _.get(listingData, 'pictureURLs');
        if (pictureURLs) {
          data.list = pictureURLs;
        }
        elementsUpdate[elementId] = {
          data: {
            $merge: data,
          },
        };
      } else {
        elementsUpdate[elementId] = {
          data: {
            $merge: {bulkReviseEnabled},
          },
        };
      }
    } else if (templateTag && element.type !== elementTypes.MobileGalleryElement) {
      if (templateTagSpecifics && templateTagSpecifics[templateTag]) {
        const data = {
          templateTagData: templateTagSpecifics[templateTag],
          listingData,
          draft,
        };
        if (listingData && listingData[templateTag]) {
          data.text = listingData[templateTag];
        } else if (draft && _.isEmpty(listingData[templateTag])) {
          data.text = data.templateTagData.draftPlaceholder;
        }
        elementsUpdate[elementId] = {
          type: {
            $set: elementTypes.TitleElement,
          },
          data: {
            $merge: data,
          },
        };
      } else {
        elementsUpdate[elementId] = {
          data: {
            templateTag: {
              $set: null
            }
          }
        };
      }
    }
  });
  return update(elements, elementsUpdate);
}

export const getNormalizedDesktopElements = createSelector(
  [getDesktopElements, getEditingElementId, getUpdatePreview, getLastAddedElementsIds, getNormalizedTemplateTagSpecifics, getListingData, isBulkReviseEnabled, isDraft],
  (desktopElements, editingElementId, updatePreview, lastAddedElementsIds, normalizedTemplateTagSpecifics, listingData, bulkReviseEnabled, draft) => {
    let spreadedDesktopElements = spreadChildren(desktopElements);
    spreadedDesktopElements = addTemplateTagsData(spreadedDesktopElements, normalizedTemplateTagSpecifics, listingData, bulkReviseEnabled, null, draft);
    if (updatePreview) {
      return applyPreview(spreadedDesktopElements, updatePreview);
    } else if (editingElementId) {
      return updateEditingElement(spreadedDesktopElements, editingElementId);
    } else if (!_.isEmpty(lastAddedElementsIds)) {
      const desktopElementsUpdate = {};
      lastAddedElementsIds.forEach((elementId) => {
        if (spreadedDesktopElements[elementId]) {
          desktopElementsUpdate[elementId] = {added: {$set: true}};
        }
      });
      return update(spreadedDesktopElements, desktopElementsUpdate);
    }
    return spreadedDesktopElements;
  }
);

export const getNormalizedMobileElements = createSelector(
  [getMobileElements, getDesktopElements, getUpdatePreview, getNormalizedTemplateTagSpecifics, getListingData, isBulkReviseEnabled, isDraft],
  (mobileElements, desktopElements, updatePreview, normalizedTemplateTagSpecifics, listingData, bulkReviseEnabled, draft) => {
    let spreadedMobileElements = spreadChildren(mobileElements);
    spreadedMobileElements = addTemplateTagsData(spreadedMobileElements, normalizedTemplateTagSpecifics, listingData, bulkReviseEnabled, desktopElements, draft);
    if (updatePreview) {
      return applyPreview(spreadedMobileElements, updatePreview);
    }
    return spreadedMobileElements;
  }
);

function prepareNewDesktopElements(elements, baseUrl, ebayAccounts) {
  const elementsUpdate = {};
  _.forEach(elements, (element, id) => {
    if (element.type === elementTypes.CrossSellGalleryElement) {
      const ebayAccount = ebayAccounts[element.data.sellerId];
      elementsUpdate[id] = {
        data: {
          baseUrl: {
            $set: baseUrl,
          },
          crossSellItemsData: {
            $set: _.get(ebayAccount, `crossSellItemsData.${element.data.globalId}`),
          }
        }
      };
    } else if (element.type === elementTypes.VideoElement) {
      elementsUpdate[id] = {
        data: {
          baseUrl: {
            $set: baseUrl,
          },
        }
      };
    }
  });
  if (!_.isEmpty(elementsUpdate)) {
    return update(elements, elementsUpdate);
  }
  return elements;
}

export const getNormalizedDesktopElementsForRender = createSelector(
  [getNormalizedDesktopElements, getBaseUrl, getEbayAccounts],
  (normalizedDesktopElements, baseUrl, ebayAccounts) => {
    let newDesktopElements = spreadChildren(normalizedDesktopElements);
    newDesktopElements = prepareNewDesktopElements(newDesktopElements, baseUrl, ebayAccounts);
    return newDesktopElements;
  }
);


function applyDragDelta(elements, dragDelta, selectedElementIds, isDraggingElement, viewState, elementsState, isResizingElement, draggingDirection) {
  if (dragDelta && (selectedElementIds || draggingDirection)) {
    const elementUpdate = {};
    const selectedElements = [];
    selectedElementIds.forEach(id => {
      const element = elements[id];
      if (element) {
        if (element.parentId) {
          selectedElements.push(element);
        } else {
          selectedElements.push(element);
        }
      }
    });
    if (_.isEmpty(selectedElements)) {
      return elements;
    }
    if (isDraggingElement && dragDelta) {
      const draggableDirections = getDraggableDirections(selectedElements);
      const maxDeltaY = getMaxDeltaY(viewState, elementsState, selectedElements);
      selectedElements.forEach((element) => {
        elementUpdate[element.id] = {
          layout: {
            $apply: layout => (
              {...layout, ...getUpdatedElementPositionByDelta(viewState, elementsState, dragDelta, element, draggableDirections, maxDeltaY)}
            ),
          },
          dragging: {
            $set: true,
          }
        };
      });
      return update(elements, elementUpdate);
    } else if (isResizingElement && dragDelta) {
      const draggableDirections = getDraggableDirections(selectedElements);
      const maxDeltaY = getMaxDeltaY(viewState, elementsState, selectedElements);
      selectedElements.forEach((element) => {
        elementUpdate[element.id] = {
          layout: {
            $apply: layout => (
            {...layout, ...getUpdatedElementSizeByDirection(viewState, elementsState, {...dragDelta, direction: draggingDirection}, element.id)}
            )
          }
        };
      });
      return update(elements, elementUpdate);
    }
  }
  return elements;
}

export const getNormalizedDesktopElementsForRenderWithDragPosition = createSelector(
  [getNormalizedDesktopElementsForRender, getDragDelta, getSelectedElementIds, isDraggingElement, getElementsState, getViewMode, isResizingElement, getDraggingDirection],
  (normalizedDesktopElementsForRender, dragDelta, selectedElementIds, isDraggingElement, elementsState, viewMode, isResizingElement, draggingDirection) => (
    applyDragDelta(normalizedDesktopElementsForRender, dragDelta, selectedElementIds, isDraggingElement, getViewState(viewMode), elementsState, isResizingElement, draggingDirection)
  )
);

export const getNormalizedStaticDesktopElements = createSelector(
  [getStaticDesktopElements, getUpdatePreview],
  (staticDesktopElements, updatePreview) =>
    applyPreview(staticDesktopElements, updatePreview)
);

export const getAllDesktopElements = createSelector(
  [getNormalizedStaticDesktopElements, getNormalizedDesktopElements],
  (normalizedStaticDesktopElements, normalizedDesktopElements) => {
    return {
      ...normalizedStaticDesktopElements,
      ...normalizedDesktopElements
    };
  }
);

export const getAllDesktopElementsByList = createSelector(
  [getNormalizedStaticDesktopElements, getNormalizedDesktopElements],
  (normalizedStaticDesktopElements, normalizedDesktopElements) => {
    return {
      staticElements: normalizedStaticDesktopElements,
      elements: normalizedDesktopElements
    };
  }
);

export const getAllMobileElements = createSelector(
  [getStaticMobileElements, getNormalizedMobileElements],
  (staticMobileElements, normalizedMobileElements) => {
    return {
      ...staticMobileElements,
      ...normalizedMobileElements
    };
  }
);

export const getAllMobileElementsByList = createSelector(
  [getStaticMobileElements, getNormalizedMobileElements],
  (staticMobileElements, normalizedMobileElements) => {
    return {
      staticElements: staticMobileElements,
      elements: normalizedMobileElements
    };
  }
);

export const getAllElementsForView = createSelector(
  [getAllDesktopElements, getAllMobileElements, isMobileView],
  (allDesktopElements, allMobileElements, isMobileView) => isMobileView ? allMobileElements : allDesktopElements
);

export const getAllVisibleElementsForView = createSelector(
  [getAllDesktopElements, getAllMobileElements, isMobileView],
  (allDesktopElements, allMobileElements, isMobileView) => isMobileView ? getVisibleElements(allMobileElements) : allDesktopElements
);

export const getStaticElementsForView = createSelector(
  [getNormalizedStaticDesktopElements, getStaticMobileElements, isMobileView],
  (normalizedStaticDesktopElements, staticMobileElements, isMobileView) => isMobileView ? staticMobileElements : normalizedStaticDesktopElements
);

export const getSelectedElementsForCopy = createSelector(
  [getSelectedElementIds, getDesktopElements, getMobileElements, isMobileView],
  (selectedElementIds, desktopElements, mobileElements, isMobileView) => {
    let selectedMobileElements;
    let selectedDesktopElements;
    if (isMobileView) {
      selectedMobileElements = extractElementsByIds(mobileElements, selectedElementIds);
      selectedDesktopElements = [];
      selectedMobileElements.forEach( element => {
        selectedDesktopElements.push(desktopElements[element.desktopId]);
      });
    } else {
      selectedDesktopElements = extractElementsByIds(desktopElements, selectedElementIds);
      selectedMobileElements = [];
      _.forEach(mobileElements, element => {
        if (selectedElementIds.indexOf(element.desktopId) !== -1) {
          selectedMobileElements.push(element);
        }
      });
    }
    return {
      desktop: selectedDesktopElements,
      mobile: selectedMobileElements,
    };
  }
);

export const getSelectedElements = createSelector(
  [getAllElementsForView, getSelectedElementIds],
  (elements, selectedElementIds) => (
    extractElementsByIds(elements, selectedElementIds)
  )
);

export const getElementsForDesktopElementControlView = createSelector(
  [getSelectedElements, getSelectedElementIds, getDesktopElements],
  (selectedElements, selectedElementIds, elements) => {
    const actualSelectedElementIds = _.isArray(selectedElementIds) ? selectedElementIds : [selectedElementIds];
    const parentId = getParentIdFromChildId(actualSelectedElementIds[0]);
    if (parentId) {
      return [_.get(elements, `[${parentId}]`)];
    }
    return selectedElements;
  }
);

export const getHiddenMobileElements = createSelector(
  [getNormalizedMobileElements],
  (normalizedMobileElements) => getHiddenElements(normalizedMobileElements)
);

export const getVisibleMobileElements = createSelector(
  [getNormalizedMobileElements],
  (normalizedMobileElements) => getVisibleElements(normalizedMobileElements)
);

export const getElementsForView = createSelector(
  [getNormalizedDesktopElements, getVisibleMobileElements, isMobileView],
  (normalizedDesktopElements, normalizedMobileElements, isMobileView) => isMobileView ? normalizedMobileElements : normalizedDesktopElements
);

export const getHoveredElement = createSelector(
  [getElementsForView, getHoveredElementId],
  (elements, hoveredElementId) => elements[hoveredElementId]
);

export const getVisibleMobileElementsForRender = createSelector(
  [getNewMobileElements, getVisibleMobileElements],
  (newMobileElements, visibleMobileElements) => {
    return {...spreadChildren(visibleMobileElements), ...newMobileElements};
  }
);

export const getVisibleMobileElementsForRenderWithDragPosition = createSelector(
  [getVisibleMobileElementsForRender, getDragDelta, getSelectedElementIds, isDraggingElement, getElementsState, getViewMode, isResizingElement, getDraggingDirection],
  (visibleMobileElementsForRender, dragDelta, selectedElementIds, isDraggingElement, elementsState, viewMode, isResizingElement, draggingDirection) => (
    applyDragDelta(visibleMobileElementsForRender, dragDelta, selectedElementIds, isDraggingElement, getViewState(viewMode), elementsState, isResizingElement, draggingDirection)
  )
);

export const getSelectedElementsWithDragPosition = createSelector(
  [getAllElementsForView, getDragDelta, getSelectedElementIds, isDraggingElement, getElementsState, getViewMode, isResizingElement, getDraggingDirection],
  (allElementsForView, dragDelta, selectedElementIds, isDraggingElement, elementsState, viewMode, isResizingElement, draggingDirection) => (
    extractElementsByIds(applyDragDelta(allElementsForView, dragDelta, selectedElementIds, isDraggingElement, getViewState(viewMode), elementsState, isResizingElement, draggingDirection), selectedElementIds)
  )
);

export const getPageElement = createSelector(
  [getStaticElementsForView],
  (staticElements) => staticElements[PAGE_ELEMENT_ID]
);

export const getPageElementLayout = createSelector(
  [getPageElement],
  (pageElement) => pageElement.layout
);

export const getSaveData = createSelector(
  [getDesktopState, getMobileState, getIsMobileOptimized],
  (desktopState, mobileState, mobileOptimized) => {
    return {
      desktopElements: JSON.stringify(desktopState),
      mobileElements: JSON.stringify(mobileState),
      mobileOptimized
    };
  }
);

export const isSelectedElementStatic = createSelector(
  [getSelectedElementIds, getStaticElementsForView],
  (selectedElementIds, staticElements) => !!(selectedElementIds.length && staticElements[selectedElementIds[0]])
);

export const isSelectedElementLocked = createSelector(
  [getElementsForDesktopElementControlView],
  (selectedElements) => !!(selectedElements.length && _.get(selectedElements[0], 'locked'))
);

export const getParentLocationIfExists = createSelector(
  [getSelectedElements, getSelectedElementIds, getDesktopElements],
  (selectedElements, selectedElementIds, elements) => {
    const actualSelectedElementIds = _.isArray(selectedElementIds) ? selectedElementIds : [selectedElementIds];
    const parentId = getParentIdFromChildId(actualSelectedElementIds[0]);
    if (parentId) {
      return {
        x: [_.get(elements, `[${parentId}].layout.x`)],
        y: [_.get(elements, `[${parentId}].layout.y`)]
      };
    }
    return false;
  }
);

export const isBackgroundElementSelected = createSelector(
  [getSelectedElements],
  (selectedElements) => !!(selectedElements.length && isBackgroundElement(selectedElements[0].id))
);

export const isPageElementSelected = createSelector(
  [getSelectedElements],
  (selectedElements) => !!(selectedElements.length && isPageElement(selectedElements[0].id))
);

